import { useState } from "react";
import { BedDouble, CalendarClock, PlaneLanding, PlaneTakeoff, ChevronDown, ChevronRight, MessageCircle } from "lucide-react";

const digits = (num) => (num || "").replace(/[^\d]/g, "");
const waLink = (num) => `https://wa.me/${digits(num)}`;

const fmtDay = (iso) => {
  if (!iso) return "Not specified";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

const sumGuests = (list) => list.reduce((s, e) => s + (e.party_size || 0), 0);

// Nights occupied = arrival up to (not including) departure, since checkout is ~11 AM IST
// on the departure day. Missing/invalid departure counts as 1 night (the arrival night).
const nightsBetween = (arrivalISO, departureISO) => {
  if (!arrivalISO) return 0;
  const a = new Date(`${arrivalISO}T00:00:00`);
  const d = departureISO ? new Date(`${departureISO}T00:00:00`) : null;
  if (!d || isNaN(d.getTime()) || d <= a) return 1;
  return Math.round((d - a) / 86400000);
};

const stayLabel = (e) => {
  const n = nightsBetween(e.arrival_date, e.departure_date);
  if (!e.arrival_date) return "";
  if (e.departure_date) return `${fmtDay(e.arrival_date)} → checkout ${fmtDay(e.departure_date)} · ${n} night${n === 1 ? "" : "s"}`;
  return `${fmtDay(e.arrival_date)} · ${n} night${n === 1 ? "" : "s"} (no departure set)`;
};

// A lightweight collapsible section for "see more details when needed".
const Collapse = ({ title, subtitle, icon: Icon, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)]" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-3 text-left"
        data-testid={testid ? `${testid}-toggle` : undefined}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
          {Icon ? <Icon className="w-4 h-4 text-[var(--gold)]" /> : null}
          {title}
          {subtitle ? <span className="text-xs font-normal text-[var(--text-muted)]">· {subtitle}</span> : null}
        </span>
        {open ? <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-muted)]" />}
      </button>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

const MiniStat = ({ label, value, sub, testid }) => (
  <div className="rounded-xl p-4 bg-[var(--surface)] border border-[var(--border)]" data-testid={testid}>
    <p className="text-2xl font-serif text-[var(--burgundy)]">{value}</p>
    <p className="text-xs text-[var(--text-muted)] uppercase tracking-wide">{label}</p>
    {sub ? <p className="text-xs text-[var(--text-secondary)] mt-0.5">{sub}</p> : null}
  </div>
);

export const StayPlanner = ({ planning }) => {
  const [open, setOpen] = useState(true);
  const [perRoom, setPerRoom] = useState(2);
  const [expandedDay, setExpandedDay] = useState(null);

  if (!planning) return null;

  const {
    stay_parties = 0, stay_guests = 0, confirmed_guests = 0, pending_guests = 0,
    pending_parties = 0, confirmed_parties = 0, nightly = [], peak_night = null,
    arrivals_by_day = {}, departures_by_day = {},
  } = planning;

  if (stay_parties === 0) {
    return (
      <div className="bg-[var(--card)] rounded-xl p-6 border border-[var(--border)]" data-testid="cms-stay-planner-empty">
        <p className="text-sm font-medium text-[var(--text-primary)] flex items-center gap-2">
          <BedDouble className="w-4 h-4 text-[var(--gold)]" /> Stay &amp; Arrivals Planner
        </p>
        <p className="text-sm text-[var(--text-muted)] mt-2">No guests need accommodation yet. When guests request a stay, a simple summary and arrival plan will appear here to help you book rooms.</p>
      </div>
    );
  }

  const per = Math.max(1, Number(perRoom) || 1);
  const rooms = (guests) => Math.ceil((guests || 0) / per);
  const peakRooms = peak_night ? rooms(peak_night.guests) : 0;
  const arrivalDays = Object.entries(arrivals_by_day);
  const departureDays = Object.entries(departures_by_day);

  return (
    <div className="bg-[var(--card)] rounded-xl border-2 border-[var(--border-gold)] overflow-hidden" data-testid="cms-stay-planner">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left"
        data-testid="cms-stay-planner-toggle"
      >
        <span className="flex items-center gap-2 font-serif text-xl text-[var(--burgundy)]">
          <BedDouble className="w-5 h-5 text-[var(--gold)]" /> Stay &amp; Arrivals Planner
        </span>
        <span className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          {stay_parties} part{stay_parties === 1 ? "y" : "ies"} · {stay_guests} guest{stay_guests === 1 ? "" : "s"}
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-6 space-y-6">
          {/* At-a-glance summary */}
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <MiniStat label="Guests needing stay" value={stay_guests} sub={`${stay_parties} part${stay_parties === 1 ? "y" : "ies"}`} testid="cms-plan-stay-guests" />
              <MiniStat label="Rooms at peak night" value={peakRooms} sub={peak_night ? `${fmtDay(peak_night.date)} · ${peak_night.guests} guests` : "—"} testid="cms-plan-peak-rooms" />
              <MiniStat label="Nights to cover" value={nightly.length} sub={`${confirmed_parties} confirmed · ${pending_parties} pending`} testid="cms-plan-nights" />
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-2">
              {confirmed_guests} guest{confirmed_guests === 1 ? "" : "s"} confirmed · {pending_guests} pending. Room estimate assumes {per} guest{per === 1 ? "" : "s"} per room.
            </p>
          </div>

          {/* Arrivals — the primary planning view */}
          <div data-testid="cms-plan-arrivals">
            <p className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
              <PlaneLanding className="w-4 h-4 text-[var(--gold)]" /> Arrivals by day
            </p>
            {arrivalDays.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)]">No arrival dates yet — see the follow-up list below.</p>
            ) : (
              <div className="space-y-2">
                {arrivalDays.map(([day, list]) => {
                  const isOpen = expandedDay === day;
                  const guests = sumGuests(list);
                  return (
                    <div key={day} className="rounded-lg border border-[var(--border)] bg-[var(--surface)]" data-testid={`cms-plan-arrival-day-${day}`}>
                      <button
                        type="button"
                        onClick={() => setExpandedDay(isOpen ? null : day)}
                        className="w-full flex items-center justify-between gap-3 p-3 text-left"
                      >
                        <span className="text-sm">
                          <span className="font-medium text-[var(--burgundy)]">{fmtDay(day)}</span>
                          <span className="text-[var(--text-secondary)]"> · {guests} guest{guests === 1 ? "" : "s"} ({list.length} part{list.length === 1 ? "y" : "ies"})</span>
                        </span>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-[var(--text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--text-muted)]" />}
                      </button>
                      {isOpen && (
                        <ul className="px-3 pb-3 space-y-1.5">
                          {list.map((e) => (
                            <li key={e.id} className="flex items-center justify-between gap-2 text-sm">
                              <span className="text-[var(--text-secondary)]">
                                {e.name} <span className="text-[var(--text-muted)]">×{e.party_size}</span>
                                {e.arrival_time ? <span className="text-[var(--text-muted)]"> · {e.arrival_time}</span> : null}
                                {e.status === "confirmed" ? <span className="text-green-600"> · confirmed</span> : <span className="text-[var(--gold-hover)]"> · pending</span>}
                                <span className="block text-xs text-[var(--text-muted)] mt-0.5">{stayLabel(e)}</span>
                              </span>
                              <a href={waLink(e.whatsapp)} target="_blank" rel="noreferrer" className="p-1 rounded hover:bg-[var(--rose)]" title="Chat on WhatsApp">
                                <MessageCircle className="w-4 h-4 text-green-600" />
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detailed breakdowns, tucked away until needed */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">More details</p>

            <Collapse title="Rooms to book, night by night" icon={CalendarClock} testid="cms-plan-details-nightly">
              <div className="flex flex-wrap items-center justify-end gap-2 mb-3">
                <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  Guests per room
                  <input
                    type="number"
                    min="1"
                    value={perRoom}
                    onChange={(e) => setPerRoom(e.target.value)}
                    className="w-16 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 py-1 text-center text-[var(--text-primary)]"
                    data-testid="cms-plan-per-room"
                  />
                </label>
              </div>
              {nightly.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">No arrival dates yet — add dates to see nightly room needs.</p>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
                  <table className="w-full text-sm" data-testid="cms-plan-nightly-table">
                    <thead>
                      <tr className="text-left text-[var(--text-muted)] text-xs uppercase tracking-wide border-b border-[var(--border)] bg-[var(--card)]">
                        <th className="p-3">Night</th>
                        <th className="p-3">Guests</th>
                        <th className="p-3">Parties</th>
                        <th className="p-3">Rooms</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nightly.map((n) => {
                        const isPeak = peak_night && n.date === peak_night.date;
                        return (
                          <tr key={n.date} className={`border-b border-[var(--border)] last:border-0 ${isPeak ? "bg-[var(--rose)]" : ""}`} data-testid={`cms-plan-night-${n.date}`}>
                            <td className="p-3 font-medium text-[var(--text-primary)]">
                              {fmtDay(n.date)} {isPeak && <span className="ml-1 text-xs text-[var(--burgundy)]">peak</span>}
                            </td>
                            <td className="p-3">{n.guests}</td>
                            <td className="p-3 text-[var(--text-secondary)]">{n.parties}</td>
                            <td className="p-3 font-serif text-lg text-[var(--burgundy)]">{rooms(n.guests)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-[var(--text-muted)] mt-2 italic">Each night is counted from the arrival day up to — but not including — the departure day (checkout ~11 AM IST). Example: arrive 2 Dec, checkout 4 Dec = nights of 2 &amp; 3 Dec. Guests without a departure date are counted for their arrival night only.</p>
            </Collapse>

            <Collapse title="Departures by day" icon={PlaneTakeoff} subtitle={`${departureDays.length} day${departureDays.length === 1 ? "" : "s"}`} testid="cms-plan-departures">
              {departureDays.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]">No departure dates yet.</p>
              ) : (
                <div className="space-y-3">
                  {departureDays.map(([day, list]) => (
                    <div key={day} data-testid={`cms-plan-departure-${day}`}>
                      <p className="text-sm font-medium text-[var(--burgundy)]">{fmtDay(day)} · {sumGuests(list)} guest{sumGuests(list) === 1 ? "" : "s"}</p>
                      <ul className="mt-1 space-y-0.5">
                        {list.map((e) => (
                          <li key={e.id} className="text-sm text-[var(--text-secondary)]">{e.name} <span className="text-[var(--text-muted)]">×{e.party_size}</span></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </Collapse>
          </div>
        </div>
      )}
    </div>
  );
};
